import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getDatabase, connectDatabaseEmulator } from 'firebase/database';
// import { initializeAppCheck, ReCaptchaV3Provider, AppCheck } from "firebase/app-check";

// Declare grecaptcha global type
declare global {
  interface Window {
    grecaptcha: any;
  }
}

// Check if we're using Firebase emulator (explicit opt-in)
const useEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true';

// Firebase configuration from environment variables
// For production, all values must be provided
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'sandbox-fenero-ai', // Default to sandbox project
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || ''
};

// Validate required config for production
if (!useEmulator) {
  const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket'];
  const missingFields = requiredFields.filter(field => !firebaseConfig[field as keyof typeof firebaseConfig]);
  
  if (missingFields.length > 0) {
    console.error(`❌ Firebase configuration missing required fields: ${missingFields.join(', ')}`);
    console.error('⚠️  Please set environment variables:');
    missingFields.forEach(field => {
      console.error(`   VITE_FIREBASE_${field.toUpperCase().replace(/([A-Z])/g, '_$1').replace(/^_/, '')}`);
    });
  }
}

// Log which environment we're using (useful for debugging)
console.log(`🔥 Firebase initialized for: ${firebaseConfig.projectId || 'NOT CONFIGURED'} (${useEmulator ? 'EMULATOR' : 'PRODUCTION'} mode)`);

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// // Initialize App Check with error handling
// let appCheck: AppCheck | undefined;

// // Function to initialize App Check
// const initializeAppCheckWithRetry = async (): Promise<AppCheck | undefined> => {
//   try {
//     // Wait for reCAPTCHA to be available
//     if (typeof window !== 'undefined' && !window.grecaptcha) {
//       console.log('Waiting for reCAPTCHA to load...');
//       await new Promise((resolve) => {
//         const checkRecaptcha = () => {
//           if (window.grecaptcha) {
//             resolve(true);
//           } else {
//             setTimeout(checkRecaptcha, 100);
//           }
//         };
//         checkRecaptcha();
//       });
//     }

//     appCheck = initializeAppCheck(app, {
//       provider: new ReCaptchaV3Provider('6LfiA4IrAAAAACCL7NPgLb7unyfokzH9H12VsSVV'),
//       isTokenAutoRefreshEnabled: true
//     });
//     console.log('App Check initialized successfully');
//     return appCheck;
//   } catch (error) {
//     console.error('Failed to initialize App Check:', error);
//     // Fallback: initialize without App Check for development
//     if (process.env.NODE_ENV === 'development') {
//       console.warn('Running in development mode without App Check');
//     }
//     return undefined;
//   }
// };

// // Initialize App Check with error suppression
// initializeAppCheckWithRetry().catch(error => {
//   console.warn('App Check initialization failed, continuing without it:', error);
// });

// // Enhanced error logging with source maps support
// const originalConsoleError = console.error;
// const originalConsoleWarn = console.warn;

// console.error = (...args) => {
//   const message = args.join(' ');
  
//   // Suppress known browser policy errors
//   if (message.includes('Permissions-Policy') || 
//       message.includes('Unrecognized feature') ||
//       message.includes('Origin trial controlled feature not enabled')) {
//     return;
//   }
  
//   // Enhanced error logging with stack traces
//   if (process.env.NODE_ENV === 'development') {
//     console.group('🚨 Enhanced Error Logging');
//     originalConsoleError.apply(console, args);
    
//     // Log additional context
//     if (args[0] instanceof Error) {
//       console.log('Error Stack:', args[0].stack);
//       console.log('Error Name:', args[0].name);
//       console.log('Error Message:', args[0].message);
//     }
    
//     console.log('Timestamp:', new Date().toISOString());
//     console.log('User Agent:', navigator.userAgent);
//     console.log('URL:', window.location.href);
//     console.groupEnd();
//   } else {
//     originalConsoleError.apply(console, args);
//   }
// };

// console.warn = (...args) => {
//   // Enhanced warning logging
//   if (process.env.NODE_ENV === 'development') {
//     console.group('⚠️ Enhanced Warning Logging');
//     originalConsoleWarn.apply(console, args);
//     console.log('Timestamp:', new Date().toISOString());
//     console.groupEnd();
//   } else {
//     originalConsoleWarn.apply(console, args);
//   }
// };

// // Add global promise rejection handler to catch unhandled errors
// window.addEventListener('unhandledrejection', (event) => {
//   // Suppress App Check related errors to prevent them from blocking the UI
//   if (event.reason && typeof event.reason === 'object' && 
//       (event.reason.message?.includes('AppCheck') || 
//        event.reason.message?.includes('ReCAPTCHA') ||
//        event.reason.code === 'appCheck/recaptcha-error')) {
//     console.warn('Suppressed App Check error:', event.reason);
//     event.preventDefault();
//     return;
//   }
  
//   // Suppress permission policy errors (Brave browser blocking Google features)
//   if (event.reason && typeof event.reason === 'object' && 
//       (event.reason.message?.includes('Permissions-Policy') ||
//        event.reason.message?.includes('browsing-topics') ||
//        event.reason.message?.includes('run-ad-auction') ||
//        event.reason.message?.includes('private-state-token') ||
//        event.reason.message?.includes('private-aggregation') ||
//        event.reason.message?.includes('attribution-reporting') ||
//        event.reason.message?.includes('join-ad-interest-group'))) {
//     console.warn('Suppressed permission policy error (Brave browser):', event.reason);
//     event.preventDefault();
//     return;
//   }
  
//   console.error('Unhandled promise rejection:', event.reason);
//   event.preventDefault(); // Prevent the default browser behavior
  
//   // Log additional context
//   if (event.reason && typeof event.reason === 'object') {
//     console.error('Error details:', event.reason);
//   }
// });

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const rtdb = getDatabase(app);

// Connect to Firebase emulators if enabled
if (useEmulator && typeof window !== 'undefined') {
  try {
    // Parse emulator hosts - handle both localhost and host.docker.internal
    const parseHost = (hostString: string) => {
      const parts = (hostString || 'localhost').split(':')
      if (parts.length === 2) {
        return { host: parts[0], port: parseInt(parts[1]) }
      }
      return { host: 'localhost', port: 5502 }
    }

    const emulatorHosts = {
      auth: parseHost(import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST || 'localhost:5503'),
      firestore: parseHost(import.meta.env.VITE_FIREBASE_EMULATOR_HOST || 'localhost:5502'),
      storage: parseHost(import.meta.env.VITE_FIREBASE_STORAGE_EMULATOR_HOST || 'localhost:5505'),
      database: parseHost(import.meta.env.VITE_FIREBASE_DATABASE_EMULATOR_HOST || 'localhost:5504'),
    };

    // Connect Auth emulator
    if (!(auth.config as any).emulator) {
      connectAuthEmulator(auth, `http://${emulatorHosts.auth.host}:${emulatorHosts.auth.port}`, { disableWarnings: true });
      console.log(`✅ Connected to Auth emulator: ${emulatorHosts.auth.host}:${emulatorHosts.auth.port}`);
    }

    // Connect Firestore emulator
    const dbSettings = (db as any)._settings
    if (!dbSettings?.host?.includes('localhost') && !dbSettings?.host?.includes('127.0.0.1') && !dbSettings?.host?.includes('host.docker.internal')) {
      connectFirestoreEmulator(db, emulatorHosts.firestore.host, emulatorHosts.firestore.port);
      console.log(`✅ Connected to Firestore emulator: ${emulatorHosts.firestore.host}:${emulatorHosts.firestore.port}`);
    }

    // Connect Storage emulator
    try {
      connectStorageEmulator(storage, emulatorHosts.storage.host, emulatorHosts.storage.port);
      console.log(`✅ Connected to Storage emulator: ${emulatorHosts.storage.host}:${emulatorHosts.storage.port}`);
    } catch (e) {
      console.warn('⚠️ Storage emulator connection failed (may already be connected):', e);
    }

    // Connect Realtime Database emulator
    try {
      connectDatabaseEmulator(rtdb, emulatorHosts.database.host, emulatorHosts.database.port);
      console.log(`✅ Connected to Database emulator: ${emulatorHosts.database.host}:${emulatorHosts.database.port}`);
    } catch (e) {
      console.warn('⚠️ Database emulator connection failed (may already be connected):', e);
    }
  } catch (error) {
    console.error('❌ Failed to connect to Firebase emulators:', error);
    console.warn('⚠️ Continuing without emulator connection');
  }
}

// export { appCheck };

export default app; 